import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import { useCheckGoogleRemediationPermissions } from "../../api/hooks/useCheckGoogleRemediationPermissions";
import { useCheckUserPermission } from "../../api/hooks/useCheckUserPermission";
import useColors from "../../api/hooks/useColors";
import {
  controlKeys,
  remediationAuditLogKeys,
  remediationKeys,
} from "../../api/queryKeys";
import SvgToIcon from "../../icons/SvgToIcon";
import { icons } from "../../scenes/stack/environment/icons/iconExport";
import { PLATFORM_USER_ACTIONS } from "../../utils/constants";
import CustomToolTipWithPermission from "../CustomToolTipWithPermission";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import LoadingIcon from "../LoadingIcon";
import { Toast } from "../Toast";

export default function RestrictGoogleFileAccessButton({
  controlId = null,
  filesToRestrict = null,
  showToolTip = true,
  disabled = false,
  ...props
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { restrictGoogleFileAccess, trackEvent } = useAPI();
  const [isRestrictDialogOpen, setIsRestrictDialogOpen] = useState(false);
  const [isRestricting, setIsRestricting] = useState(false);

  const { permitted: hasUserPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.RESTRICT_GOOGLE_FILE_ACCESS
  );

  const {
    hasRemediationPermissions,
    isLoading: isCheckingRemediationPermissions,
  } = useCheckGoogleRemediationPermissions();

  const hasAllPermissions = hasUserPermission && hasRemediationPermissions;
  const isButtonDisabled =
    disabled ||
    !hasAllPermissions ||
    isCheckingRemediationPermissions ||
    isRestricting ||
    filesToRestrict.length <= 0;

  const handleRestrict = async () => {
    trackEvent("User clicked on 'Restrict Google file access' button").then();
    setIsRestrictDialogOpen(true);
  };

  const handleRestrictConfirmButton = async () => {
    trackEvent(
      "User clicked on 'Confirm' button for restricting Google file access"
    ).then();

    handleCloseDialog();
    setIsRestricting(true);
    Toast.info("Restricting Google file access...");

    try {
      if (filesToRestrict) {
        const restrictedFilesCountData = await restrictGoogleFileAccess(
          controlId,
          filesToRestrict
        );

        const restrictedFilesCount = restrictedFilesCountData.data;
        const expectedRestrictedFilesCount = filesToRestrict.length;

        if (restrictedFilesCount === expectedRestrictedFilesCount) {
          Toast.success(
            `Successfully restricted access for ${restrictedFilesCount}/${expectedRestrictedFilesCount} Google files`
          );
        } else if (restrictedFilesCount > 0) {
          Toast.info(
            `Restricted access for ${restrictedFilesCount}/${expectedRestrictedFilesCount} Google files`
          );
        } else {
          Toast.error(
            "Unable to restrict Google file access. Please contact support for assistance"
          );
        }

        // Refresh the misconfigurations table
        queryClient.invalidateQueries({
          queryKey: controlKeys.all,
        });
        if (controlId) {
          queryClient.invalidateQueries({
            queryKey: remediationAuditLogKeys.auditLogs(controlId),
          });
        }
        queryClient.invalidateQueries({
          queryKey: remediationKeys.all,
        });
      }
    } catch (error) {
      Toast.error("Failed to restrict Google file access");
    } finally {
      setIsRestricting(false);
    }
  };

  const handleCloseDialog = () => {
    setIsRestrictDialogOpen(false);
  };

  const getTooltipMessage = () => {
    if (!showToolTip) return "";
    if (!hasUserPermission)
      return 'Your role, "Viewer", does not have permission to perform this action.';
    if (!hasRemediationPermissions) {
      return "Google remediation permissions are required. Please configure them in the Google integration settings.";
    }
    if (isCheckingRemediationPermissions) {
      return "Checking permissions...";
    }
    if (isRestricting) {
      return "Restricting file access...";
    }
    return "Restrict access for selected files";
  };

  return (
    <>
      <CustomToolTipWithPermission
        title={getTooltipMessage()}
        permission={hasUserPermission}
        displayPermissionDeniedMessage={false}
        placement="top"
      >
        <Box>
          <Button
            sx={{
              color: colors.text_primary,
              backgroundColor: colors.common_white,
              width: "32px",
              height: "32px",
              minWidth: "auto",
              border: "1px solid " + colors.enabled_stroke,
              textTransform: "unset",
              borderRadius: "8px",
              cursor: "pointer",
              ...props.sx,
            }}
            onClick={handleRestrict}
            disabled={isButtonDisabled}
          >
            <Box style={{ visibility: isRestricting ? "hidden" : "visible" }}>
              <SvgToIcon
                IconLogo={icons.RestrictFileAccessIcon}
                style={{
                  width: "14px",
                  color: isButtonDisabled
                    ? colors.action_disabled
                    : colors.icon_action_active,
                }}
              />
            </Box>
            {isRestricting && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <LoadingIcon type="circular" size={14} />
              </Box>
            )}
          </Button>
        </Box>
      </CustomToolTipWithPermission>

      {/* Restrict confirmation dialog */}
      <ConfirmationDialog
        openDialog={isRestrictDialogOpen}
        handleDialogClose={handleCloseDialog}
        handleConfirm={handleRestrictConfirmButton}
        dialogTitle="Restrict Google file access"
        dialogContentFirstLine="Are you sure you want to make these files private?"
        dialogContentSecondLine="The files will no longer be publicly accessible."
        confirmationButtonText="Make Private"
        destructive={true}
      />
    </>
  );
}
